import { ChevronLeft as ArrowBackIosIcon, ChevronRight as ArrowForwardIosIcon, ShoppingCart } from "lucide-react";
import React, { useEffect, useRef, useState } from "react";
import { useCart } from "../../../../../context/CartContext";
import { useDomain } from "../../../../../context/DomainContext";
import { addProductToCart } from "../../../../products/ProductToCart.jsx";
import FormatPrice from "../../../../../components/FormatPrice.jsx";
import RetailRichTextDescription from "../../../../Templates/RetailRichTextDescription";
import { useDispatch } from "react-redux";
import { setSelectedProduct } from "../../../../../api/reducers/selectedProductReducer";

const ScrollableFeaturedProducts = ({ productList, navigate, loggedIn }) => {
  const { addToCart, updateCart } = useCart();
  const domain = useDomain();
  const dispatch = useDispatch();
  const scrollContainerRef = useRef(null);
  const [atStart, setAtStart] = useState(true);
  const [atEnd, setAtEnd] = useState(false);

  const viewProductDetail = (product) => {
    if (loggedIn) {
      dispatch(setSelectedProduct(product));
      navigate("/product-info");
    } else {
      navigate("/sign-in");
    }
  };

  const handleProductClick = (product) => {
    if (loggedIn) {
      if (domain === "EDUCATION") {
        viewProductDetail(product);
      } else {
        addProductToCart({
          addToCart,
          navigate,
          productDetails: product,
          quantity: 1,
          updateCart,
        });
      }
    } else {
      navigate("/sign-in");
    }
  };

  const scrollByProductCount = (count) => {
    const productWidth = 250; // Adjust width based on product card width
    return productWidth * count;
  };

  const scrollLeft = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({
        left: -scrollByProductCount(2), // Reduced scroll amount for smaller screens
        behavior: "smooth",
      });
    }
  };

  const scrollRight = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({
        left: scrollByProductCount(2),
        behavior: "smooth",
      });
    }
  };

  const checkScrollPosition = () => {
    if (scrollContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
      setAtStart(scrollLeft === 0);
      setAtEnd(scrollLeft + clientWidth >= scrollWidth);
    }
  };

  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    if (scrollContainer) {
      scrollContainer.addEventListener("scroll", checkScrollPosition);
      checkScrollPosition();
    }
    return () => {
      if (scrollContainer) {
        scrollContainer.removeEventListener("scroll", checkScrollPosition);
      }
    };
  }, []);

  return (
    <div className="relative mt-8">
      <h2 className="text-3xl sm:text-4xl font-bold mb-4 text-center text-gray-800">
        Featured Products
      </h2>
      <div className="flex items-center relative">
        {!atStart && (
          <button
            className="absolute left-0 bg-white bg-opacity-80 p-2 rounded-full shadow-lg z-10 hover:bg-opacity-100 transition duration-200"
            onClick={scrollLeft}
          >
            <ArrowBackIosIcon fontSize="large" />
          </button>
        )}
        <div
          ref={scrollContainerRef}
          className="flex overflow-x-auto scrollbar-hide space-x-4 px-4"
          style={{
            scrollSnapType: "x mandatory",
            scrollBehavior: "smooth",
          }}
        >
          {productList &&
            productList.map((productItem) => (
              <div
                key={productItem?._id}
                className="min-w-[220px] md:min-w-[250px] max-w-[250px] bg-white shadow-md p-4 rounded-lg overflow-hidden transform hover:scale-105 transition-transform duration-300"
                style={{ minHeight: "440px", height: "auto", scrollSnapAlign: "start" }}
              >
                <img
                  loading="lazy"
                  src={productItem?.imageUrlsList?.[0] || "/default-image.jpg"}
                  alt={productItem?.name || "Product Image"}
                  className="h-40 md:h-48 object-cover mb-2 w-full rounded-md shadow-lg transition-transform duration-300 transform hover:scale-105"
                />
                <h3
                  className="text-md md:text-lg font-semibold cursor-pointer text-[#B88E2F] hover:text-[#A67C2A]"
                  onClick={() => viewProductDetail(productItem)}
                >
                  {productItem.name}
                </h3>
                <RetailRichTextDescription content={productItem.description} className="mt-1 md:mt-2" />
                <div className="mt-1 md:mt-2 text-lg md:text-2xl font-medium">
                  {productItem.price && (
                    <FormatPrice
                      code={productItem.price.currency || "USD"}
                      price={productItem.price.priceAmt}
                    />
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => handleProductClick(productItem)}
                  className="mt-2 bg-[#B88E2F] text-white rounded-full px-3 py-2 w-full flex items-center justify-center hover:bg-[#A67C2A] transition duration-200 text-sm md:text-base"
                >
                  <span>{domain === "EDUCATION" ? "View Schedule" : "Add to Cart"}</span>
                  <ShoppingCart className="ml-2" />
                </button>
              </div>
            ))}
        </div>
        {!atEnd && (
          <button
            className="absolute right-0 bg-white bg-opacity-80 p-2 rounded-full shadow-lg z-10 hover:bg-opacity-100 transition duration-200"
            onClick={scrollRight}
          >
            <ArrowForwardIosIcon fontSize="large" />
          </button>
        )}
      </div>
    </div>
  );
};

export default ScrollableFeaturedProducts;
